import { useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  Avatar,
  ListItemAvatar,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import EventIcon from "@mui/icons-material/Event";
import StarIcon from "@mui/icons-material/Star";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import PersonIcon from "@mui/icons-material/Person";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useStatistics } from "@hooks/useStatistics";
import { StatMetricCard } from "@components/StatMetricCard";
import { PageScaffold } from "@components/PageScaffold";
import {
  CHART_CONTAINER_HEIGHT_PX,
  chartColorSequence,
  lineChartPrimaryStroke,
  StatAccent,
} from "@/constants";

export default function Analytics() {
  const { t } = useTranslation();
  const { stats, loading } = useStatistics();
  const na = t("common.notAvailable");

  const statCards = useMemo(
    () => [
      {
        label: t("analytics.totalVisits"),
        value: stats?.totalEvents ?? na,
        icon: EventIcon,
        color: StatAccent.coral,
      },
      {
        label: t("analytics.avgRating"),
        value:
          stats?.averageRating != null ? stats.averageRating.toFixed(1) : na,
        icon: StarIcon,
        color: StatAccent.amber,
      },
      {
        label: t("analytics.estSpend"),
        value: stats?.totalRestaurants ?? na,
        icon: RestaurantIcon,
        color: StatAccent.green,
      },
      {
        label: t("analytics.topCompanion"),
        value:
          stats?.topCompanion?.user?.displayName?.trim() ||
          t("analytics.topCompanion"),
        icon: PersonIcon,
        color: StatAccent.blue,
        sub: stats?.topCompanion
          ? `${stats.topCompanion.sharedMeals} ${t("analytics.sharedMeals")}`
          : undefined,
      },
    ],
    [stats, t, na],
  );

  if (loading) {
    return (
      <PageScaffold>
        <Skeleton variant="rounded" height={400} />
      </PageScaffold>
    );
  }

  return (
    <PageScaffold>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 700,
        }}
      >
        {t("analytics.title")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3,
        }}
      >
        {t("analytics.subtitle")}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((s) => (
          <Grid key={s.label} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatMetricCard
              icon={s.icon}
              color={s.color}
              label={s.label}
              value={s.value}
              sub={s.sub}
            />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 7 }} sx={{ minWidth: 0 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t("analytics.ratingsTrend")}
              </Typography>
              {stats?.ratingsTrend && stats.ratingsTrend.length > 0 ? (
                <Box
                  sx={{
                    width: "100%",
                    height: CHART_CONTAINER_HEIGHT_PX,
                    minWidth: 0,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.ratingsTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" fontSize={12} />
                      <YAxis domain={[0, 5]} fontSize={12} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="average"
                        stroke={lineChartPrimaryStroke}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {t("common.noData")}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }} sx={{ minWidth: 0 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t("analytics.categoryDistribution")}
              </Typography>
              {stats?.categoryDistribution &&
              stats.categoryDistribution.length > 0 ? (
                <Box
                  sx={{
                    width: "100%",
                    height: CHART_CONTAINER_HEIGHT_PX,
                    minWidth: 0,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.categoryDistribution}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                      >
                        {stats.categoryDistribution.map((_, i) => (
                          <Cell
                            key={i}
                            fill={
                              chartColorSequence[i % chartColorSequence.length]
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {t("common.noData")}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("analytics.mostVisited")}
          </Typography>
          {stats?.mostVisited && stats.mostVisited.length > 0 ? (
            <List disablePadding>
              {stats.mostVisited.map((item, i) => (
                <ListItem
                  key={item.restaurant.id}
                  disablePadding
                  sx={{ py: 0.5 }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor:
                          chartColorSequence[i % chartColorSequence.length],
                        fontSize: 14,
                      }}
                    >
                      {i + 1}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.restaurant.name}
                    secondary={`${item.visitCount} ${t("analytics.visits")}`}
                    slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              {t("common.noData")}
            </Typography>
          )}
        </CardContent>
      </Card>
    </PageScaffold>
  );
}
